import React, { useState, useEffect } from "react";

/* --- 1. Helper for Titles --- */
function getConversationTitle(conv) {
  if (conv.title) return conv.title;
  if (conv.messages && conv.messages.length > 0) {
     const firstUserMsg = conv.messages.find(m => m.role === "user");
     return firstUserMsg?.text?.slice(0, 45) || "New conversation";
  }
  return "New conversation";
}

export default function Sidebar({
  isAuthed,
  conversations = [],
  activeConvId,
  sidebarCollapsed,
  toggleSidebar,
  onNewConversation,
  onOpenConversation,
  onDeleteConversation 
}) {
  
  const [deletingId, setDeletingId] = useState(null);

  // Stop scroll propagation
  function stopWheel(e) { e.stopPropagation(); }

  // Delete handlers
  function handleDeleteClick(e, id) {
    e.stopPropagation(); 
    setDeletingId(id);
  }

  function confirmDelete() {
    if (deletingId) {
      onDeleteConversation(deletingId);
      setDeletingId(null);
    }
  }

  const visibleConversations = conversations;
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);
  const minSwipeDistance = 50;
  const onTouchStart = (e) => {
    setTouchEnd(null); 
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;

    // If swiped left (dragged towards edge), collapse sidebar
    if (isLeftSwipe) {
      toggleSidebar(true); // true = collapse
    }
  };
  return (
    
    <>
      {/* === MOBILE TOGGLE BUTTON === 
        Only visible on Mobile (hidden md:), and only when sidebar is collapsed/closed.
        This represents the "symbol" you mentioned.
      */}
      <button 
        onClick={toggleSidebar}
        className={`
           fixed top-4 left-4 z-30 p-2 rounded-md bg-[#faf2d9] shadow-md text-2xl
           md:hidden 
           ${!sidebarCollapsed ? 'hidden' : 'block'}
        `}
      >
        ≡
      </button>

      {/* === MOBILE OVERLAY ===
        Darkens the background when sidebar is OPEN on mobile.
        Clicking it closes the sidebar.
      */}
      <div 
        onClick={toggleSidebar}
        className={`
          fixed inset-0 z-30 bg-black/50 backdrop-blur-sm transition-opacity duration-300 md:hidden
          ${!sidebarCollapsed ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
        `}
      />

      {/* === SIDEBAR CONTAINER === 
      */}
      <aside
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={`
          fixed left-0 z-40 flex flex-col
          bg-[#faf2d9] border-r border-black/5 shadow-[0_4px_20px_rgba(0,0,0,0.02)]
          transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] overflow-hidden

          /* --- MOBILE STYLES (Unchanged) --- */
          top-0 h-full w-[280px]
          ${sidebarCollapsed ? "-translate-x-full" : "translate-x-0"}

          /* --- DESKTOP STYLES (Updated) --- */
          md:top-16
          md:mt-13   /* Your custom spacing (52px) */
          
          /* SHAPE FIXES */
          md:h-[calc(100vh-6rem-1rem)]  /* Height = Viewport - Header - BottomGap */
          md:rounded-b-[20px]           /* Adds the circular/rounded bottom */
          md:rounded-t-[20px]
          md:border-b md:border-black/5
          
          md:translate-x-0
          ${sidebarCollapsed ? "md:w-[64px]" : "md:w-[320px]"}
        `}
      >        
        {/* === HEADER SECTION === */}
        <div className="flex flex-col items-stretch gap-2.5 shrink-0 px-3 pt-[18px] pb-2.5">
          
          {/* Hamburger Row (Inside Sidebar) */}
          <div className="flex w-full justify-start">
            <button
              onClick={() => toggleSidebar()}
              title="Toggle Menu"
              className="h-9 w-9 flex items-center justify-center rounded-full bg-transparent text-xl text-[#07102a] hover:bg-black/5 transition-colors"
            >
              ≡
            </button>
          </div>

          {/* Brand Name (Hidden if collapsed) */}
          <div className={`
            font-dunhill text-[22px] font-extrabold text-[#07102a] text-center pb-1 cursor-default
            whitespace-nowrap overflow-hidden transition-opacity duration-200
            ${sidebarCollapsed ? "opacity-0 h-0" : "opacity-100 h-auto"}
          `}>
            GnosisFT
          </div>

          {/* New Chat Button */}
          {isAuthed && (
            <button
              onClick={onNewConversation}
              className={`
                flex items-center gap-2.5 rounded-lg border-0 bg-transparent cursor-pointer transition-colors hover:bg-black/5
                ${sidebarCollapsed ? "justify-center p-0 h-10 w-10" : "justify-start px-3 py-2.5 w-full"}
              `}
            >
              <span className="text-xl">📜</span>
              <span className={`
                 text-base font-bold text-[#07102a] whitespace-nowrap overflow-hidden transition-all duration-200
                 ${sidebarCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"}
              `}>
                New Chat
              </span>
            </button>
          )}
        </div>

        {/* === CONVERSATION LIST === */}
        <div 
          className={`
            flex-1 overflow-y-auto min-h-0 px-2 py-1
            scrollbar-thin scrollbar-thumb-black/10 scrollbar-track-transparent
            transition-opacity duration-200
            ${sidebarCollapsed ? "md:opacity-0 md:pointer-events-none" : "opacity-100 pointer-events-auto"}
          `}
        >
          {visibleConversations.length === 0 && (
             // Only show text if not collapsed (prevents weird squishing)
            !sidebarCollapsed && (
              <div className="mt-5 text-center text-sm text-[#07102a]/55">
                No history yet
              </div>
            )
          )}

          {visibleConversations.map((c) => {
            const active = c.id === activeConvId;
            return (
              <div
                key={c.id}
                onClick={() => {
                   onOpenConversation(c.id);
                   // OPTIONAL: Auto-close sidebar on mobile when a chat is clicked
                   if (window.innerWidth < 768) toggleSidebar(); 
                }}
                className={`
                  group relative mb-1 h-12 w-full cursor-pointer overflow-hidden rounded-lg transition-colors
                  ${active 
                    ? "bg-gradient-to-b from-[#5d5cff14] to-[#5d5cff0a] shadow-[0_2px_4px_rgba(93,92,255,0.04)]" 
                    : "hover:bg-black/5"
                  }
                `}
              >
                <div className="flex h-full w-full transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:-translate-x-[40px]">
                  <div className="flex min-w-full items-center gap-3 px-2.5">
                     <div className="flex w-6 justify-center text-base">📝</div>
                     <div className="flex flex-1 flex-col overflow-hidden">
                       <div className="truncate font-sans text-[13px] font-normal leading-tight text-[#07102a]">
                         {getConversationTitle(c)}
                       </div>
                     </div>
                  </div>

                  <button
                     onClick={(e) => handleDeleteClick(e, c.id)}
                     title="Delete conversation"
                     className="flex h-full w-[40px] min-w-[40px] items-center justify-center bg-transparent text-base text-[#07102a] hover:bg-[#ff4d4d] hover:text-white transition-colors"
                  >
                     🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* --- CONFIRMATION MODAL (Keep as is) --- */}
      {deletingId && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
          <div className="w-[300px] rounded-xl bg-white p-6 text-center text-[#333] shadow-[0_10px_25px_rgba(0,0,0,0.1)]">
            <h3 className="mt-0 text-[#d32f2f] text-lg font-bold">Delete Chat?</h3>
            <p className="mb-5 text-sm text-[#666]">
              This will permanently delete this conversation and all its messages.
            </p>
            <div className="flex justify-between gap-2.5">
              <button 
                className="flex-1 cursor-pointer rounded-md border border-[#ddd] bg-[#f5f5f5] p-2.5 text-[#333] hover:bg-gray-200" 
                onClick={() => setDeletingId(null)}
              >
                Cancel
              </button>
              <button 
                className="flex-1 cursor-pointer rounded-md border-none bg-[#d32f2f] p-2.5 font-bold text-white hover:bg-[#b71c1c]" 
                onClick={confirmDelete}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}